/* ============================================================
   Dathent — Dashboard (new, pack-aligned)
   Replaces previous Dashboard. Real-data driven: no fabricated
   counters per HANDOFF §2.1. Scenarios (active/calm/firstrun)
   derived from useDashboardData + ContentFeedContext.activeBatches.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { api } from '../services/api/client.js';
import {
  ActiveJobsWidget,
  AttentionPanel,
  AutopilotWidget,
  EngagementChart,
  FirstRunHero,
  Greeting,
  StatCards,
  WeekStrip,
} from '../components/dashboard';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function startOfWeek(d = new Date()) {
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday-start
  const s = new Date(d);
  s.setDate(d.getDate() + diff);
  s.setHours(0, 0, 0, 0);
  return s;
}

function buildWeekDays(scheduleItems = [], calendarItems = []) {
  const monday = startOfWeek();
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = dayLabels.map((label, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return {
      label,
      date: `${date.toLocaleString('en', { month: 'short' })} ${date.getDate()}`,
      today: date.getTime() === today.getTime(),
      _date: date,
      slots: [],
    };
  });

  const slotKey = (idx, platform, time) => `${idx}|${platform}|${time}`;
  const seen = new Set();

  // 1. Calendar items (scheduled_posts with targets) — richer: contains all statuses
  for (const item of calendarItems) {
    const post = item?.post || item;
    const when = post?.scheduledAt || post?.scheduled_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const dayDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const idx = days.findIndex((x) => x._date.toDateString() === dayDate.toDateString());
    if (idx === -1) continue;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const time = `${hh}:${mm}`;
    // contentItemId surfaces under slightly different keys depending on
    // which endpoint feeds us (scheduler vs calendar). Try them in order.
    const contentItemId = post?.contentItemId || post?.content_item_id || item?.contentItemId || item?.content_item_id || null;
    const targets = item?.targets || [];
    if (targets.length === 0) {
      // No targets yet — single slot keyed off post status
      const platform = PLATFORM_FROM_NAME(post.platform);
      const status = post.status === 'published' ? 'done' : 'scheduled';
      const key = slotKey(idx, platform, time);
      if (!seen.has(key)) {
        seen.add(key);
        days[idx].slots.push({ platform, status, time, contentItemId, targetId: null });
      }
    } else {
      for (const t of targets) {
        const platform = PLATFORM_FROM_NAME(t.platform);
        const tStatus = t.status;
        const status = tStatus === 'published'
          ? 'done'
          : (tStatus === 'live' || (Math.abs(d - new Date()) < 3600000 && d <= new Date()))
            ? 'live'
            : 'scheduled';
        const key = slotKey(idx, platform, time);
        if (!seen.has(key)) {
          seen.add(key);
          days[idx].slots.push({ platform, status, time, contentItemId, targetId: t?.id || null });
        }
      }
    }
  }

  // 2. Fallback to schedule items (upcoming/planned) — merges with calendar if not seen
  for (const item of scheduleItems) {
    const when = item?.scheduledAt || item?.scheduled_at || item?.publishAt || item?.publish_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const dayDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const idx = days.findIndex((x) => x._date.toDateString() === dayDate.toDateString());
    if (idx === -1) continue;
    const platform = PLATFORM_FROM_NAME(item.platform || item.platformId || item.platform_name);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const time = `${hh}:${mm}`;
    const key = slotKey(idx, platform, time);
    if (seen.has(key)) continue;
    seen.add(key);
    const itemStatus = item.status || item.post?.status;
    const status = itemStatus === 'published'
      ? 'done'
      : (itemStatus === 'live' || (Math.abs(d - new Date()) < 3600000 && d <= new Date()))
        ? 'live'
        : 'scheduled';
    const contentItemId = item?.contentItemId || item?.content_item_id || item?.post?.contentItemId || item?.post?.content_item_id || null;
    days[idx].slots.push({ platform, status, time, contentItemId, targetId: item?.targetId || item?.target_id || null });
  }

  // Sort slots within each day by time
  for (const day of days) {
    day.slots.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }

  return days.map(({ _date, ...rest }) => rest);
}

function scheduledNext7Days(scheduleItems = []) {
  const now = new Date();
  const horizon = new Date(now);
  horizon.setDate(now.getDate() + 7);
  return scheduleItems.filter((item) => {
    const when = item?.scheduledAt || item?.scheduled_at || item?.publishAt || item?.publish_at;
    if (!when) return false;
    const d = new Date(when);
    return d >= now && d <= horizon;
  });
}

function buildChecklist(state) {
  const { publishingPrefs, socialAccounts } = state;
  return [
    {
      label: 'Connect social accounts',
      sub: socialAccounts?.length
        ? `${socialAccounts.length} platform${socialAccounts.length === 1 ? '' : 's'} connected`
        : 'No platforms connected yet',
      done: (socialAccounts?.length || 0) > 0,
      cta: 'Connect',
      to: '/app/settings?tab=social',
    },
    {
      label: 'Configure autopilot mode',
      sub: publishingPrefs?.autopilotMode
        ? `Current: ${publishingPrefs.autopilotMode === 'auto' ? 'Full autopilot' : 'Approve before publish'}`
        : 'Choose how Dathent publishes for you',
      done: Boolean(publishingPrefs?.autopilotMode),
      cta: 'Configure',
      to: '/app/settings?tab=preferences',
    },
    {
      label: 'Generate first batch',
      sub: 'Posts ready in minutes, scheduled into your slots',
      done: false,
      cta: 'Generate',
      to: '/app/create',
    },
  ];
}

function deriveAttentionItems(state, navigate) {
  const items = [];
  const accounts = state.socialAccounts || [];

  // 1. No accounts connected → suggest connecting before generation hits a wall
  if (accounts.length === 0) {
    items.push({
      kind: 'info',
      title: 'No social accounts connected yet',
      sub: 'Connect at least one platform so generated posts can publish automatically.',
      action: 'Connect',
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  } else {
    // 2. Suggest broadening reach when very few platforms are linked
    const platforms = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()).filter(Boolean));
    if (platforms.size === 1) {
      items.push({
        kind: 'info',
        title: 'Reach more channels',
        sub: 'You\'re publishing to one platform — connect another to multiply impressions.',
        action: 'Add platform',
        onAction: () => navigate('/app/settings?tab=accounts'),
      });
    }
  }

  // 3. Accounts that explicitly need reconnect (revoked / token expired / needs_manual_action)
  const needsReconnect = accounts.filter((a) => {
    const s = String(a?.status || '').toLowerCase();
    return s === 'needs_reconnect' || s === 'needs_manual_action' || s === 'expired' || s === 'token_expired';
  });
  for (const acc of needsReconnect) {
    items.push({
      kind: 'warn',
      title: `${acc.displayName || acc.platform || 'Account'} needs to reconnect`,
      sub: 'Token is no longer valid — reconnect to resume publishing.',
      action: 'Reconnect',
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  }

  // 4. Tokens expiring soon (within 7 days)
  const expiring = accounts.filter((a) => {
    const expiresAt = a?.tokenExpiresAt || a?.token_expires_at;
    if (!expiresAt) return false;
    const days = (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days < 7;
  });
  for (const acc of expiring) {
    items.push({
      kind: 'warn',
      title: `${acc.platform || 'Account'} token expires soon`,
      sub: 'Reconnect to keep publishing without interruption.',
      action: 'Reconnect',
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  }

  return items;
}

export const Dashboard = () => {
  const { user } = useAuth();
  const dashState = useDashboardData();
  const { activeBatches, counts } = useContentFeedContext();
  const navigate = useNavigate();
  const [postsSummary, setPostsSummary] = useState(null);
  const [postsSummaryLoading, setPostsSummaryLoading] = useState(false);
  const [calendarItems, setCalendarItems] = useState([]);

  useEffect(() => {
    dashState.ensureDashboardData?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch analytics summary (published counts, avg engagement, series)
  useEffect(() => {
    let cancelled = false;
    setPostsSummaryLoading(true);
    api.analytics
      .getPostsSummary({ days: 30 })
      .then((res) => {
        if (cancelled) return;
        // API client may unwrap { data: ... } or return raw — handle both
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setPostsSummary(payload);
      })
      .catch(() => {
        if (cancelled) return;
        setPostsSummary(null);
      })
      .finally(() => {
        if (!cancelled) setPostsSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch calendar items for current week (includes published + scheduled w/ targets)
  useEffect(() => {
    let cancelled = false;
    const monday = startOfWeek();
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 7);
    api.scheduler
      .getCalendar(monday.toISOString(), sunday.toISOString())
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setCalendarItems(payload?.posts || []);
      })
      .catch(() => {
        if (!cancelled) setCalendarItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeBatch = useMemo(() => {
    const running = (activeBatches || []).find((b) => ['queued', 'generating', 'reserved'].includes(b.status));
    return running || null;
  }, [activeBatches]);

  const scenario = useMemo(() => {
    if (activeBatch) return 'active';
    const hasSchedule = (dashState.schedule || []).length > 0;
    const hasSocial = (dashState.socialAccounts || []).length > 0;
    if (!hasSchedule && !hasSocial) return 'firstrun';
    return 'calm';
  }, [activeBatch, dashState.schedule, dashState.socialAccounts]);

  // NOTE: Dashboard used to publish its OWN activity here, but
  // BatchActivityBridge (mounted globally inside AppShell) already does
  // exactly this — and it includes activity.batchId so clicking the top
  // pill opens the batch detail screen. Dashboard's duplicate setActivity
  // call was clobbering the bridge's value with a copy that lacked
  // batchId, leaving the pill unclickable (user could see "Batch
  // generation / Composing visuals" but no way to navigate into it,
  // and the "Open batch" button in the panel disappeared because its
  // render condition is `activity.batchId && ...`).
  // Bridge is now the single source of truth for batch activity state.

  const weekDays = useMemo(() => buildWeekDays(dashState.schedule || [], calendarItems), [dashState.schedule, calendarItems]);
  const scheduledSoon = useMemo(() => scheduledNext7Days(dashState.schedule || []), [dashState.schedule]);

  const stats = useMemo(() => {
    const c = counts || {};
    const out = [];

    // Published · 30d window (with delta) — preferred over all-time count when analytics data is available
    if (postsSummary && typeof postsSummary.publishedCount === 'number') {
      out.push({
        label: 'Published · 30d',
        value: postsSummary.publishedCount,
        unit: '',
        delta: typeof postsSummary.publishedDeltaPct === 'number' ? postsSummary.publishedDeltaPct : undefined,
        sub: postsSummary.publishedPrevCount > 0
          ? `vs ${postsSummary.publishedPrevCount} prev period`
          : (postsSummary.publishedCount === 0 ? 'No posts in last 30 days' : 'First-time window'),
      });
    } else {
      out.push({
        label: 'Published',
        value: c.published || 0,
        unit: 'posts',
        sub: (c.published || 0) === 0 ? 'No posts published yet' : 'Total across all platforms',
      });
    }

    // Avg engagement (when analytics has data)
    if (postsSummary && typeof postsSummary.avgEngagement === 'number') {
      out.push({
        label: 'Avg engagement',
        value: postsSummary.avgEngagement.toFixed(1),
        unit: '%',
        sub: 'Across posts with metrics',
      });
    }

    // In pipeline (real-time)
    const inPipeline = (c.generating || 0) + (activeBatch ? Math.max(0, (activeBatch.total_items || activeBatch.totalItems || 0) - (activeBatch.ready_count || activeBatch.readyCount || 0)) : 0);
    if (inPipeline > 0 || activeBatch) {
      out.push({
        label: 'In pipeline',
        value: inPipeline,
        unit: 'posts',
        sub: activeBatch ? 'Batch generating now' : 'Queued for generation',
      });
    }

    // Ready to schedule
    out.push({
      label: 'Ready to schedule',
      value: c.ready || 0,
      unit: 'posts',
      sub: (c.ready || 0) === 0 ? 'Schedule from Library' : 'Awaiting slot assignment',
    });

    // Scheduled · 7d
    out.push({
      label: 'Scheduled · 7d',
      value: scheduledSoon.length,
      unit: 'posts',
      sub: scheduledSoon.length === 0 ? 'No upcoming posts' : 'Across connected platforms',
    });

    // Cap to 4 — most important first
    return out.slice(0, 4);
  }, [counts, scheduledSoon, activeBatch, postsSummary]);

  // Build EngagementChart series from analytics posts-summary
  const engagementSeries = useMemo(() => {
    if (!postsSummary?.series?.length) return [];
    return postsSummary.series.map((row) => {
      const d = new Date(row.date);
      const label = isNaN(d.getTime())
        ? row.date
        : `${d.toLocaleString('en', { month: 'short' })} ${d.getDate()}`;
      return {
        label,
        engagement: typeof row.engagement === 'number' ? row.engagement : 0,
        published: row.published || 0,
      };
    });
  }, [postsSummary]);

  const attentionItems = useMemo(() => deriveAttentionItems(dashState, navigate), [dashState, navigate]);
  const checklist = useMemo(() => buildChecklist(dashState), [dashState]);
  const greetingName = user?.name || user?.fullName || (user?.email ? user.email.split('@')[0] : null);

  return (
    <div className="dash-root">
      <div className="dash-top-row">
        <Greeting scenario={scenario} name={greetingName} />
      </div>

      {/* Autopilot panel — visible on all non-firstrun scenarios. The
          widget owns its own loading + state via /api/autopilot/state;
          we just mount it. Toggle ON/OFF persists per user. */}
      {scenario !== 'firstrun' && <AutopilotWidget />}

      {scenario === 'active' && activeBatch && (
        <ActiveJobsWidget
          job={{
            project: activeBatch.label || activeBatch.title || activeBatch.topic || 'Content batch',
            current: activeBatch.ready_count || activeBatch.readyCount || 0,
            total: activeBatch.total_items || activeBatch.totalItems || 0,
            step: (activeBatch.generating_count || activeBatch.generatingCount) > 0 ? 'Generating' : 'Preparing',
          }}
          onOpenBatch={() => navigate(activeBatch.id ? `/app/batch/${activeBatch.id}` : '/app/library?filter=generating')}
        />
      )}

      {stats.length > 0 && <StatCards items={stats} />}

      <div className="dash-grid-2">
        <EngagementChart
          data={engagementSeries}
          partial={postsSummaryLoading || (postsSummary && engagementSeries.length > 0 && engagementSeries.every((d) => d.engagement === 0))}
        />
        <WeekStrip days={weekDays} />
      </div>

      {scenario === 'firstrun' && <FirstRunHero checklist={checklist} />}

      {scenario !== 'firstrun' && <AttentionPanel items={attentionItems} allClear={attentionItems.length === 0} />}
    </div>
  );
};

export default Dashboard;
